import * as React from 'react'
import {
  ChevronDown, ChevronRight, Copy, EyeOff, Layers3, ListFilter,
  MoreHorizontal, Plus, Search, ShieldCheck, Trash2, X,
} from 'lucide-react'
import type { FieldSpec, RuleCondition, RuleGroup, RuleNode } from '@/lib/types'
import { isGroup } from '@/lib/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  CLASS_META, MAX_DEPTH, MAX_NODES, ValueEditor, defaultValueFor,
  findNodeError, operatorLabel, preferredOperator, type TreeContext,
} from './RuleTree'

type Stage = 1 | 2 | 3

interface GuidedRuleBuilderProps {
  root: RuleGroup
  ctx: TreeContext
}

const STAGES: Array<{
  id: Stage
  title: string
  description: string
  badge: string
  icon: React.ComponentType<{ className?: string }>
  tone: 'blue' | 'violet' | 'amber'
}> = [
  {
    id: 1,
    title: '候选范围',
    description: '先圈定要扫描的域名空间，这些条件会直接降低扫描成本。',
    badge: '生成约束',
    icon: Layers3,
    tone: 'blue',
  },
  {
    id: 2,
    title: '特征筛选',
    description: '在本地计算和筛选域名特征，不产生外部查询费用。',
    badge: '本地计算',
    icon: ListFilter,
    tone: 'violet',
  },
  {
    id: 3,
    title: '外部确认',
    description: '只对通过前两步的候选查询外部状态，费用和等待时间更可控。',
    badge: '可能消耗额度',
    icon: ShieldCheck,
    tone: 'amber',
  },
]

export function GuidedRuleBuilder({ root, ctx }: GuidedRuleBuilderProps) {
  const supportsGuidedMode = root.op === 'AND' && !root.not
  const activeChildren = root.children.filter((node) => !node.disabled)
  const summary = describeNodes(activeChildren, 'AND', ctx.fields)

  if (!supportsGuidedMode) {
    return (
      <Card className="border-amber-300 bg-amber-500/[0.06] p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-700">
            <MoreHorizontal className="h-4 w-4" aria-hidden />
          </div>
          <div>
            <p className="text-sm font-semibold">这条规则需要高级逻辑模式</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              根分组使用了“任一满足”或整体取反，无法安全拆成三个顺序阶段。请切换到“高级逻辑”编辑，规则内容不会发生变化。
            </p>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.08] via-background to-violet-500/[0.06] p-4 shadow-sm">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Badge variant="default">当前规则</Badge>
          <span className="text-xs text-muted-foreground">自动转成易读说明</span>
        </div>
        <p className="text-base font-medium leading-7">
          {summary ? `查找${summary}的域名。` : '还没有条件，请从下方选择一个条件开始。'}
        </p>
      </div>

      {STAGES.map((stage) => {
        const entries = root.children
          .map((node, index) => ({ node, index, path: `$.root.children[${index}]` }))
          .filter(({ node }) => stageForNode(node, ctx.fields) === stage.id)

        return (
          <StageCard key={stage.id} {...stage}>
            {entries.length ? (
              entries.map(({ node, index, path }) => (
                <GuidedNode
                  key={node.id ?? path}
                  node={node}
                  path={path}
                  depth={2}
                  ctx={ctx}
                  stage={stage.id}
                  index={index}
                  parentGroup={root}
                />
              ))
            ) : (
              <div className="rounded-lg border border-dashed px-4 py-5 text-center">
                <p className="text-xs text-muted-foreground">这个阶段还没有条件</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              <AddFieldButton
                fields={fieldsForStage(ctx.fields, stage.id)}
                onSelect={(fieldName) => ctx.onAddCondition('$.root', fieldName)}
                disabled={ctx.nodeCount >= MAX_NODES}
              />
              {stage.id === 2 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => ctx.onAddGroup('$.root')}
                  disabled={ctx.nodeCount >= MAX_NODES}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden /> 添加“任一满足”分组
                </Button>
              )}
            </div>
          </StageCard>
        )
      })}
    </div>
  )
}

function GuidedNode({
  node, path, depth, ctx, stage, index, parentGroup,
}: {
  node: RuleNode
  path: string
  depth: number
  ctx: TreeContext
  stage: Stage
  index: number
  parentGroup: RuleGroup
}) {
  if (isGroup(node)) {
    return <GuidedGroup group={node} path={path} depth={depth} ctx={ctx} stage={stage} />
  }
  return <GuidedCondition condition={node} path={path} depth={depth} ctx={ctx} index={index} parentGroup={parentGroup} />
}

function GuidedGroup({
  group, path, depth, ctx, stage,
}: {
  group: RuleGroup
  path: string
  depth: number
  ctx: TreeContext
  stage: Stage
}) {
  const [collapsed, setCollapsed] = React.useState(false)
  const error = ctx.errors[path] || ctx.errors[`${path}.children`] || ctx.errors[`${path}.op`]

  function update(patch: Partial<RuleGroup>) {
    ctx.onChange(path, (node) => ({ ...(node as RuleGroup), ...patch }))
  }

  return (
    <div data-rule-path={path} className={cn(
      'overflow-visible rounded-lg border-l-4 bg-muted/20',
      stage === 3 ? 'border-l-amber-500' : 'border-l-violet-500',
      group.disabled && 'opacity-50',
      error && 'ring-1 ring-destructive',
    )}>
      <div className="flex flex-wrap items-center gap-2 border-b bg-background/70 px-3 py-2.5">
        <Button variant="ghost" size="icon-sm" onClick={() => setCollapsed((value) => !value)} aria-label={collapsed ? '展开分组' : '折叠分组'}>
          {collapsed ? <ChevronRight className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
        </Button>
        <div className="inline-flex rounded-md border bg-background p-0.5" role="group" aria-label="分组逻辑">
          <button
            type="button"
            onClick={() => update({ op: 'AND' })}
            className={cn('rounded px-2.5 py-1 text-xs', group.op === 'AND' && 'bg-primary text-primary-foreground')}
            aria-pressed={group.op === 'AND'}
          >
            全部满足
          </button>
          <button
            type="button"
            onClick={() => update({ op: 'OR' })}
            className={cn('rounded px-2.5 py-1 text-xs', group.op === 'OR' && 'bg-violet-600 text-white')}
            aria-pressed={group.op === 'OR'}
          >
            任一满足
          </button>
        </div>
        <span className="text-xs text-muted-foreground">以下 {group.children.length} 项</span>
        {group.not && <Badge variant="danger">整体排除</Badge>}
        <div className="ml-auto flex items-center gap-0.5">
          <Tooltip content={group.not ? '取消整体排除' : '对整个分组取反'}>
            <Button variant="ghost" size="sm" onClick={() => update({ not: !group.not })}>
              {group.not ? '取消排除' : '整体排除'}
            </Button>
          </Tooltip>
          <Button variant="ghost" size="icon-sm" onClick={() => update({ disabled: !group.disabled })} aria-label={group.disabled ? '启用分组' : '停用分组'}>
            <EyeOff className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => ctx.onChange(path, () => null)} aria-label="删除分组">
            <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
          </Button>
        </div>
      </div>

      {error && <p role="alert" className="bg-destructive/5 px-3 py-2 text-xs text-destructive">{humanizeValidationMessage(error)}</p>}

      {!collapsed && (
        <div className="space-y-2 p-3">
          {group.children.map((child, index) => (
            <GuidedNode
              key={child.id ?? `${path}-${index}`}
              node={child}
              path={`${path}.children[${index}]`}
              depth={depth + 1}
              ctx={ctx}
              stage={stage}
              index={index}
              parentGroup={group}
            />
          ))}
          {!group.children.length && (
            <p className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">空分组无法保存</p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <AddFieldButton
              fields={ctx.fields.filter((field) => !field.deferred)}
              onSelect={(fieldName) => ctx.onAddCondition(path, fieldName)}
              disabled={ctx.nodeCount >= MAX_NODES}
              label="添加组内条件"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => ctx.onAddGroup(path)}
              disabled={depth >= MAX_DEPTH || ctx.nodeCount >= MAX_NODES}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden /> 嵌套分组
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function GuidedCondition({
  condition, path, depth, ctx, parentGroup,
}: {
  condition: RuleCondition
  path: string
  ctx: TreeContext
  index: number
  depth: number
  parentGroup: RuleGroup
}) {
  const spec = ctx.fields.find((field) => field.name === condition.field)
  const operators = spec ? Object.keys(spec.operators) : []
  const valueKind = spec?.operators[condition.operator]
  const error = findNodeError(ctx.errors, path)
  const remainder = repeatRemainder(condition, ctx.labelLength)
  const remainderCovered = remainder
    ? siblingCoversRemainder(parentGroup, condition, remainder)
    : false

  function update(patch: Partial<RuleCondition>) {
    ctx.onChange(path, (node) => ({ ...(node as RuleCondition), ...patch }))
  }

  function changeField(fieldName: string) {
    const nextSpec = ctx.fields.find((field) => field.name === fieldName)
    const operator = preferredOperator(nextSpec)
    update({ field: fieldName, operator, value: defaultValueFor(nextSpec, operator) })
  }

  function addRemainderRule() {
    if (!remainder) return
    ctx.onChange(path, (node) => ({
      type: 'group',
      op: 'AND',
      not: false,
      id: crypto.randomUUID(),
      children: [
        node,
        {
          type: 'condition',
          field: 'repeat_pattern',
          operator: 'IS',
          value: {
            scope: remainder.scope,
            segment_start: 1,
            segment_length: remainder.length,
            unit_length: remainder.length,
            repeats: 1,
            min_repeats: 0,
          },
          id: crypto.randomUUID(),
        },
      ],
    }))
  }

  return (
    <div data-rule-path={path} className={cn(
      'relative rounded-lg border bg-background p-2.5 transition-colors hover:border-primary/30',
      condition.disabled && 'opacity-50',
      error && 'border-destructive',
    )}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="min-w-[185px] sm:w-[220px]">
          <FieldPicker fields={ctx.fields} value={condition.field} onSelect={changeField} />
        </div>

        {operators.length > 1 && (
          <select
            value={condition.operator}
            onChange={(event) => {
              const operator = event.target.value
              update({ operator, value: defaultValueFor(spec, operator) })
            }}
            className="h-9 min-w-[110px] rounded-md border border-input bg-muted/60 px-2.5 text-xs font-medium"
            aria-label="运算符"
          >
            {operators.map((operator) => <option key={operator} value={operator}>{operatorLabel(operator)}</option>)}
          </select>
        )}

        <div className="min-w-0 flex-1">
          <ValueEditor
            kind={valueKind}
            spec={spec}
            value={condition.value}
            onChange={(value) => update({ value })}
            invalid={Boolean(ctx.errors[`${path}.value`])}
          />
        </div>

        <div className="flex shrink-0 items-center gap-0.5 self-end sm:self-auto">
          {condition.not && <Badge variant="danger">取反</Badge>}
          <Tooltip content={condition.not ? '取消条件取反' : '对该条件取反'}>
            <Button variant="ghost" size="icon-sm" onClick={() => update({ not: !condition.not })} aria-label={condition.not ? '取消条件取反' : '对该条件取反'}>
              <span className="text-[10px] font-bold">NOT</span>
            </Button>
          </Tooltip>
          <Button variant="ghost" size="icon-sm" onClick={() => ctx.onDuplicate(path)} aria-label="复制条件">
            <Copy className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => update({ disabled: !condition.disabled })} aria-label={condition.disabled ? '启用条件' : '停用条件'}>
            <EyeOff className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => ctx.onChange(path, () => null)} aria-label="删除条件">
            <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
          </Button>
        </div>
      </div>
      {error && <p role="alert" className="mt-1.5 text-xs text-destructive">{humanizeValidationMessage(error)}</p>}
      {remainder && (
        <div className={cn(
          'mt-2 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs',
          remainderCovered ? 'border-emerald-200 bg-emerald-500/[0.06]' : 'border-dashed bg-muted/35',
        )}>
          <span className="font-medium">{remainder.label}</span>
          <span className="text-muted-foreground">
            {remainderCovered ? '已设置规则，并与当前片段同时满足' : '未设置规则，仅受字符集等全局条件限制'}
          </span>
          {!remainderCovered && remainder.length >= 2 && (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={addRemainderRule}
              disabled={ctx.nodeCount + 2 > MAX_NODES || depth >= MAX_DEPTH}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden /> 设置剩余位规则（AND）
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

interface RepeatRemainder {
  scope: 'prefix' | 'suffix'
  length: number
  label: string
}

function repeatRemainder(condition: RuleCondition, labelLength?: number): RepeatRemainder | null {
  if (condition.field !== 'repeat_pattern' || condition.disabled || condition.not) return null
  if (!labelLength || !Number.isInteger(labelLength)) return null
  const opts = condition.value as { scope?: string; segment_length?: number }
  const segmentLength = Number(opts.segment_length)
  if (!Number.isInteger(segmentLength) || segmentLength < 1 || segmentLength >= labelLength) return null
  const remainderLength = labelLength - segmentLength
  if (opts.scope === 'prefix') {
    return {
      scope: 'suffix',
      length: remainderLength,
      label: `剩余后 ${remainderLength} 位（第 ${segmentLength + 1}–${labelLength} 位）`,
    }
  }
  if (opts.scope === 'suffix') {
    return {
      scope: 'prefix',
      length: remainderLength,
      label: `剩余前 ${remainderLength} 位（第 1–${remainderLength} 位）`,
    }
  }
  return null
}

function siblingCoversRemainder(
  parent: RuleGroup,
  condition: RuleCondition,
  remainder: RepeatRemainder,
): boolean {
  if (parent.op !== 'AND' || parent.not || parent.disabled) return false
  return parent.children.some((node) => {
    if (isGroup(node) || node === condition || node.disabled || node.not || node.field !== 'repeat_pattern') return false
    const opts = node.value as { scope?: string; segment_length?: number }
    return opts.scope === remainder.scope && Number(opts.segment_length) === remainder.length
  })
}

function StageCard({
  id, title, description, badge, icon: Icon, tone, children,
}: {
  id: Stage
  title: string
  description: string
  badge: string
  icon: React.ComponentType<{ className?: string }>
  tone: 'blue' | 'violet' | 'amber'
  children: React.ReactNode
}) {
  const tones = {
    blue: 'bg-blue-600 text-white ring-blue-100 dark:ring-blue-950',
    violet: 'bg-violet-600 text-white ring-violet-100 dark:ring-violet-950',
    amber: 'bg-amber-500 text-white ring-amber-100 dark:ring-amber-950',
  }
  const badgeVariants = { blue: 'default', violet: 'secondary', amber: 'warning' } as const

  return (
    <Card className="overflow-visible">
      <div className="flex items-start gap-3 rounded-t-lg border-b bg-muted/25 p-4">
        <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-4', tones[tone])}>
          <Icon className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold">{id}. {title}</h2>
            <Badge variant={badgeVariants[tone]} className="text-[10px]">{badge}</Badge>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="space-y-2 p-3 sm:p-4">{children}</div>
    </Card>
  )
}

function AddFieldButton({
  fields, onSelect, disabled, label = '添加条件',
}: {
  fields: FieldSpec[]
  onSelect: (fieldName: string) => void
  disabled?: boolean
  label?: string
}) {
  const [open, setOpen] = React.useState(false)
  return (
    <div className="relative">
      <Button variant="outline" size="sm" onClick={() => setOpen((value) => !value)} disabled={disabled}>
        <Plus className="h-3.5 w-3.5" aria-hidden /> {label}
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </Button>
      {open && (
        <FieldMenu
          fields={fields}
          onClose={() => setOpen(false)}
          onSelect={(fieldName) => {
            onSelect(fieldName)
            setOpen(false)
          }}
        />
      )}
    </div>
  )
}

function FieldPicker({ fields, value, onSelect }: { fields: FieldSpec[]; value: string; onSelect: (value: string) => void }) {
  const [open, setOpen] = React.useState(false)
  const spec = fields.find((field) => field.name === value)
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex h-9 w-full items-center rounded-md border border-input bg-background px-3 text-left text-sm font-medium hover:bg-accent/40"
        aria-expanded={open}
      >
        <span className="truncate">{spec?.label ?? '选择字段'}</span>
        <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      </button>
      {open && (
        <FieldMenu
          fields={fields}
          onClose={() => setOpen(false)}
          onSelect={(fieldName) => {
            onSelect(fieldName)
            setOpen(false)
          }}
        />
      )}
    </div>
  )
}

function FieldMenu({ fields, onSelect, onClose }: { fields: FieldSpec[]; onSelect: (value: string) => void; onClose: () => void }) {
  const [query, setQuery] = React.useState('')
  const filtered = fields.filter((field) =>
    `${field.label}${field.description}${CLASS_META[field.class].label}`.toLowerCase().includes(query.toLowerCase()),
  )

  return (
    <div className="absolute left-0 top-10 z-30 w-[min(420px,calc(100vw-3rem))] rounded-xl border bg-popover p-2 shadow-xl">
      <div className="mb-2 flex items-center gap-1">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索长度、重复、状态……" className="pl-9" />
        </div>
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="关闭字段选择">
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      <div className="max-h-72 space-y-1 overflow-y-auto">
        {filtered.map((field) => (
          <button
            key={field.name}
            type="button"
            disabled={field.deferred}
            onClick={() => onSelect(field.name)}
            className="flex w-full items-start gap-3 rounded-lg px-2.5 py-2 text-left hover:bg-accent disabled:cursor-not-allowed disabled:opacity-45"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">{field.label}</span>
              <span className="block truncate text-xs text-muted-foreground">{field.deferred ? field.deferred_note : field.description}</span>
            </span>
            <Badge variant={field.class === 'external_state' ? 'warning' : field.class === 'generation_constraint' ? 'default' : 'muted'} className="shrink-0 text-[10px]">
              {CLASS_META[field.class].label}
            </Badge>
          </button>
        ))}
        {!filtered.length && <p className="p-4 text-center text-xs text-muted-foreground">没有匹配的字段</p>}
      </div>
    </div>
  )
}

function fieldsForStage(fields: FieldSpec[], stage: Stage) {
  return fields.filter((field) => {
    if (field.deferred) return false
    if (stage === 1) return field.class === 'generation_constraint'
    if (stage === 3) return field.class === 'external_state'
    return field.class !== 'generation_constraint' && field.class !== 'external_state'
  })
}

function stageForNode(node: RuleNode, fields: FieldSpec[]): Stage {
  if (!isGroup(node)) {
    const className = fields.find((field) => field.name === node.field)?.class
    if (className === 'generation_constraint') return 1
    if (className === 'external_state') return 3
    return 2
  }
  if (!node.children.length) return 2
  const stages = new Set(node.children.map((child) => stageForNode(child, fields)))
  if (stages.size === 1) return [...stages][0]
  return 2
}

function describeNodes(nodes: RuleNode[], op: 'AND' | 'OR', fields: FieldSpec[]): string {
  const descriptions = nodes.map((node) => describeNode(node, fields)).filter(Boolean)
  return descriptions.join(op === 'AND' ? '，且' : '，或')
}

function describeNode(node: RuleNode, fields: FieldSpec[]): string {
  if (node.disabled) return ''
  if (isGroup(node)) {
    const featureNames = node.children.map((child) => featureName(child, fields))
    if (node.op === 'OR' && featureNames.every(Boolean)) {
      const content = `满足“${featureNames.join(' / ')}”任一特征`
      return node.not ? `排除${content}` : content
    }
    const content = describeNodes(node.children, node.op, fields)
    if (!content) return ''
    if (node.not && node.children.length === 1) return `排除${content}`
    return node.not ? `排除同时满足（${content}）的结果` : `满足（${content}）`
  }
  const spec = fields.find((field) => field.name === node.field)
  const label = spec?.label ?? node.field
  const value = describeValue(node.value, spec)
  let content = `${label}${operatorLabel(node.operator)}${value}`
  if (node.field === 'tld') {
    const values = Array.isArray(node.value) ? node.value : [node.value]
    content = `域名后缀为${values.map((item) => `.${String(item).replace(/^\./, '')}`).join('、')}`
  } else if (node.field === 'label_length' && node.operator === 'EQ') {
    content = `域名长度为 ${String(node.value)} 位`
  } else if (node.field === 'charset' && node.operator === 'EQ') {
    content = `字符类型为${value}`
	} else if (node.field === 'repeat_pattern') {
		const opts = node.value as {
			unit_length?: number; repeats?: number; min_repeats?: number
			scope?: string; segment_start?: number; segment_length?: number
		}
		const scope = opts.scope === 'prefix'
			? `前 ${opts.segment_length ?? 0} 位`
			: opts.scope === 'suffix'
				? `后 ${opts.segment_length ?? 0} 位`
				: opts.scope === 'range'
					? `从第 ${opts.segment_start ?? 1} 位起的 ${opts.segment_length ?? 0} 位`
					: '整个域名'
		const count = opts.repeats ? `精确重复 ${opts.repeats} 次` : `至少重复 ${opts.min_repeats ?? 2} 次`
		content = opts.repeats === 1 && (opts.unit_length ?? 0) >= 2
			? `${scope}由同一字符连续组成 ${opts.unit_length} 位`
			: `${scope}按重复长度 ${opts.unit_length || '任意'}、${count}排列（支持 ABABAB / AABBCC 两类）`
	} else if (node.field === 'sequence') {
		const opts = node.value as { direction?: string; min_length?: number; step?: number }
		const direction = opts.direction === 'asc' ? '递增' : opts.direction === 'desc' ? '递减' : '递增或递减'
		content = `存在至少 ${opts.min_length ?? opts.step ?? 4} 位连续${direction}片段`
	} else if (node.field === 'grouped_digit_sequence') {
		const opts = node.value as { group_size?: number }
		content = `每 ${opts.group_size ?? 2} 个相同字符组成一组，相邻组字符不同`
  } else if (node.field === 'lifecycle_status') {
    content = `生命周期状态为${value}`
  }
  return node.not ? `不满足“${content}”` : content
}

function featureName(node: RuleNode, fields: FieldSpec[]): string {
  if (isGroup(node) || node.disabled || node.not) return ''
  if (node.field === 'pattern') return describeValue(node.value, fields.find((field) => field.name === node.field)).replaceAll('“', '').replaceAll('”', '')
  if (node.field === 'palindrome' && node.value === true) return '回文'
	if (node.field === 'grouped_digit_sequence') return '成组重复结构'
  return ''
}

function describeValue(value: unknown, spec?: FieldSpec): string {
  const renderOne = (item: unknown) => {
    const option = spec?.enum?.find((entry) => entry.value === String(item))
    return option?.label ?? String(item)
  }
  if (Array.isArray(value)) return `“${value.map(renderOne).join('、')}”`
  if (typeof value === 'boolean') return value ? '“是”' : '“否”'
  if (value && typeof value === 'object') return `“${Object.values(value as Record<string, unknown>).join(' / ')}”`
  return `“${renderOne(value)}”`
}

function humanizeValidationMessage(message: string): string {
  const exact: Record<string, string> = {
    'group must contain at least one child': '分组不能为空，请至少添加一个条件',
    'must contain at least one value': '请至少选择或输入一个值',
    'must not be empty': '条件值不能为空',
    'required': '缺少必填内容',
    'must be greater than zero': '数值必须大于 0',
  }
  return exact[message] ?? message
}
